import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProductInListData } from 'types/ProductListTypes';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table';
import { IconNote } from 'shared/components/ui/IconNote';

import styles from './ProductListTable.module.scss';
import { formatItalianNumber } from 'utils/formatUtils';

type ProductListTableProps = {
  products: ProductInListData[];
  onDeleteProduct?: (isin: string) => void;
  isEditable?: boolean;
};

export const ProductListTable: React.FC<ProductListTableProps> = ({
  products,
  onDeleteProduct,
  isEditable = false,
}) => {
  const { t } = useTranslation();

  const formatPercentage = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return formatItalianNumber(value, 2);
  };

  const getScoreDisplay = (finalScore: number | null, finalScoreAdvice: number | null): string => {
    if (finalScore === null && finalScoreAdvice === null) return '-';
    if (finalScore !== null && finalScoreAdvice !== null) {
      return `${formatItalianNumber(finalScore, 0)} / ${formatItalianNumber(finalScoreAdvice, 0)}`;
    }
    if (finalScore !== null) return formatItalianNumber(finalScore, 0);
    if (finalScoreAdvice !== null) return formatItalianNumber(finalScoreAdvice, 0);
    return '-';
  };

  return (
    <>
      {products.some((p) => !!p.consultability_status && p.consultability_status !== 'consultable') && (
        <IconNote
          type="attention"
          title={t('instrumentLists.disabledProductsWarningTitle')}
          body={t('instrumentLists.disabledProductsWarningBody')}
        />
      )}
      <div className={styles.tableContainer}>
      <div className={styles.tableWrapper}>
        <Table className={styles.table}>
          <TableHeader>
            <TableRow className={styles.headerRow}>
              <TableHead className={styles.colIsin}>{t('instrumentLists.table.isin')}</TableHead>
              <TableHead className={styles.colTipoProdotto}>{t('instrumentLists.table.tipoProdotto')}</TableHead>
              <TableHead className={styles.colNome}>{t('instrumentLists.table.nome')}</TableHead>
              <TableHead className={styles.colDivisa}>{t('instrumentLists.table.valuta')}</TableHead>
              <TableHead className={styles.colConsultable}>{t('constraints.product.modal.table.consultable')}</TableHead>
              <TableHead className={styles.colMaintainable}>{t('constraints.product.modal.table.maintainable')}</TableHead>
              <TableHead className={styles.colMicrocategoria}>{t('instrumentLists.table.microcategoria')}</TableHead>
              <TableHead className={styles.colCosti}>{t('instrumentLists.table.costiTotaliStimati')}</TableHead>
              <TableHead className={styles.colRend}>{t('instrumentLists.table.rend1y')} (%)</TableHead>
              <TableHead className={styles.colRend}>{t('instrumentLists.table.rend3y')} (%)</TableHead>
              <TableHead className={styles.colDividend}>{t('instrumentLists.table.dividendYield')}</TableHead>
              <TableHead className={styles.colScore}>{t('instrumentLists.table.finalScore')}</TableHead>
              <TableHead className={styles.colEsg}>{t('instrumentLists.table.ratingEsg')}</TableHead>
              {isEditable && <TableHead className={styles.deleteColumn}></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isEditable ? 14 : 13} className={styles.emptyCell}>
                  {t('instrumentLists.table.noProducts')}
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => {
                const isDisabled = !!product.consultability_status && product.consultability_status !== 'consultable';
                return (
                <TableRow key={product.isin} className={`${styles.dataRow} ${isDisabled ? styles.disabledRow : ''}`}>
                  <TableCell className={`${styles.cell} ${styles.colIsin}`}>{product.isin}</TableCell>
                  <TableCell className={`${styles.cell} ${styles.colTipoProdotto}`}>
                    {product.tipo_prodotto || '-'}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colNome}`}>{product.nome || '-'}</TableCell>
                  <TableCell className={`${styles.cell} ${styles.colDivisa}`}>{product.divisa || '-'}</TableCell>
                  <TableCell className={`${styles.cell} ${styles.colConsultable}`}>
                    {product.consultability_status === 'consultable' ? 'Si' : 'No'}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colMaintainable}`}>
                    {product.consultability_status === 'maintainable' ? 'Si' : 'No'}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colMicrocategoria}`}>
                    {product.microcategoria || '-'}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colCosti}`}>
                    {formatPercentage(product.costi_totali_stimati)}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colRend}`}>
                    {formatPercentage(product.rend_1y)}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colRend}`}>
                    {formatPercentage(product.rend_3y)}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colDividend}`}>
                    {formatPercentage(product.dividend_yield)}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colScore}`}>
                    {getScoreDisplay(product.final_score, product.final_score_advice)}
                  </TableCell>
                  <TableCell className={`${styles.cell} ${styles.colEsg}`}>
                    {formatItalianNumber(product.rating_esg, 1)}
                  </TableCell>

                  {isEditable && (
                    <TableCell className={styles.deleteColumn}>
                      <button
                        className={styles.deleteButton}
                        onClick={() => onDeleteProduct?.(product.isin)}
                        title={t('instrumentLists.table.deleteProduct')}
                      >
                        ×
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              );})
            )}
          </TableBody>
        </Table>
      </div>
    </div>
    </>
  );
};
